
import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import WhyChooseUs from "@/components/shared/WhyChooseUs";
import { trackEvent } from "@/utils/tagManagerUtils";
import { getAuthUrl } from "@/utils/redirectUtils";

const PricingCTA = () => {
  const handleCTAClick = () => {
    trackEvent('pricing_cta_click');
    window.location.href = getAuthUrl();
  };

  const handleHowItWorksClick = () => {
    trackEvent('pricing_how_it_works_click');
  };

  const features = [
    "Free Plan with full access",
    "Easy setup in minutes",
    "Cancel anytime with no commitments",
    "Expert support during your trial"
  ];

  return (
    <section className="py-16 bg-primary/5">
      <div className="container mx-auto px-4 md:px-6">
        <div className="mx-auto max-w-3xl text-center">
          <motion.h2 
            className="text-3xl font-bold tracking-tighter sm:text-4xl mb-6"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            Ready to Optimize Your Google Ads?
          </motion.h2>
          
          <motion.p 
            className="text-lg text-muted-foreground mb-8"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            viewport={{ once: true }}
          >
            Join hundreds of businesses that are saving time and improving results with our AI-powered platform.
          </motion.p>
          
          <motion.div 
            className="mb-10"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <WhyChooseUs condensed={true} showTitle={false} />
          </motion.div>
          
          <motion.div 
            className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8 max-w-lg mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            viewport={{ once: true }}
          >
            {features.map((feature, index) => (
              <div key={index} className="flex items-center gap-2 text-sm">
                <Check className="h-4 w-4 text-primary flex-shrink-0" />
                <span>{feature}</span>
              </div>
            ))}
          </motion.div>
          
          <motion.div 
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            viewport={{ once: true }}
          >
            <Button 
              size="lg" 
              className="relative overflow-hidden group"
              onClick={handleCTAClick}
            >
              <span className="relative z-10">Start Your Free Account</span>
              <span className="absolute inset-0 bg-primary/0 group-hover:bg-primary/10 transition-all duration-300"></span>
            </Button>
            <Button 
              variant="outline" 
              className="group"
              onClick={handleHowItWorksClick}
            >
              <a href="/how-it-works" className="flex items-center">
                See How It Works
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </a>
            </Button>
          </motion.div>
          
          <motion.p 
            className="mt-6 text-sm text-muted-foreground"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            viewport={{ once: true }}
          >
            Cancel anytime during your trial period.
          </motion.p>
        </div>
      </div>
    </section>
  );
};

export default PricingCTA;
